/** @jsxImportSource @emotion/react */
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { Button, Divider, List, message, Modal, Spin } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import AndroidAutomation from "src/assets/installation/Android.png";
import AndroidAutomationV2 from "src/assets/installation/Appium.png";
import Studio from "src/assets/installation/icon-edit.png";
import Robot from "src/assets/installation/icon-robot.png";
import IOSAutomation from "src/assets/installation/iOS.png";
import OCR from "src/assets/installation/OCR.png";
import UpdatePackage from "src/containers/installation/UpdatePackage";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import {
  InstallPackageList,
  ProductSkuContract,
} from "src/models/installation";
import { InstallationServiceClient } from "src/services/InstallationService";

export default React.memo(() => {
  const formatMessage = useFormatMessage();
  const [dataSource, setDataSource] = useState<InstallPackageList[]>([]);
  const [isModalShow, setISModalShow] = useState(false);
  const [packageData, setPackageData] = useState({} as InstallPackageList);
  const [loading, setLoading] = useState(false);

  let isUnmounted = false;
  const list = useCallback(() => {
    InstallationServiceClient.list(2, ProductSkuContract.Enterprise).then(
      (res) => {
        if (!isUnmounted) {
          const installation: InstallPackageList[] = [];
          const studio = res.data.find(
            (item: InstallPackageList) => item.productName === "Studio"
          );
          const robot = res.data.find(
            (item: InstallPackageList) => item.productName === "Robot"
          );
          const andriod = res.data.find(
            (item: InstallPackageList) =>
              item.productName === "AndroidAutomation"
          );
          const ios = res.data.find(
            (item: InstallPackageList) => item.productName === "IOSAutomation"
          );
          const appium = res.data.find(
            (item: InstallPackageList) =>
              item.productName === "AndroidAutomationV2"
          );
          const ocr = res.data.find(
            (item: InstallPackageList) => item.productName === "OCROffline"
          );
          installation.push(studio, robot, andriod, ios, appium, ocr);
          setDataSource(installation.filter((item) => item !== undefined));
          setLoading(false);
        }
      }
    );
  }, [isUnmounted]);

  useEffect(() => {
    //获取列表
    setLoading(true);
    list();

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isUnmounted = true;
    };
  }, [list]);

  const onIsUpdate = useCallback((record: InstallPackageList) => {
    setISModalShow(true);
    setPackageData(record);
  }, []);

  const onClose = useCallback(() => {
    setISModalShow(false);
  }, []);

  const onDelete = useCallback(
    (record: InstallPackageList) => {
      Modal.confirm({
        title: formatMessage(messageIds.common.tip),
        icon: <ExclamationCircleOutlined />,
        content: formatMessage(messageIds.installation.common.deleteInstation, {
          name: record?.packageName as string,
        }),
        okText: formatMessage(messageIds.common.confirm),
        cancelText: formatMessage(messageIds.common.cancel),
        onOk() {
          InstallationServiceClient.deleteInstallation(2, record?.id).then(
            () => {
              list();
              message.success(
                formatMessage(messageIds.installation.common.deleteSuccessfully)
              );
            }
          );
        },
      });
    },
    [formatMessage, list]
  );

  return (
    <div
      css={css`
        flex-grow: 1;
        padding: 30px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-right: 30px;
          padding-bottom: 30px;
          border-bottom: 1px solid rgb(202, 209, 224);
        `}
      >
        <div
          css={css`
            max-width: 1000px;
            margin-right: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          <h1
            css={css`
              position: relative;
              margin: 0px 0px 8px;
              padding-left: 10px;
              line-height: 18px;
              font-size: 18px;
              font-weight: 600;
              color: rgb(61, 73, 102);
              ::before {
                content: "";
                position: absolute;
                left: 0px;
                width: 3px;
                height: 18px;
                background: rgb(51, 119, 255);
              }
            `}
          >
            {formatMessage(messageIds.menu.installation.name)}
          </h1>
        </div>
      </div>
      {loading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 80vh;
          `}
        >
          <Spin tip="Loading..."></Spin>
        </div>
      ) : (
        <div
          css={css`
            margin-top: 30px;
          `}
        >
          <List
            grid={{
              gutter: 30,
            }}
            dataSource={dataSource}
            renderItem={(item) => (
              <div>
                <List.Item>
                  <div
                    key={item?.id}
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      justify-content: center;
                      width: 200px;
                      height: 226px;
                      border-radius: 2px;
                      border: 1px solid rgb(235, 235, 235);
                    `}
                  >
                    <img
                      alt=""
                      src={
                        item?.productName === "Studio"
                          ? Studio
                          : item?.productName === "Robot"
                          ? Robot
                          : item?.productName === "AndroidAutomation"
                          ? AndroidAutomation
                          : item?.productName === "IOSAutomation"
                          ? IOSAutomation
                          : item?.productName === "AndroidAutomationV2"
                          ? AndroidAutomationV2
                          : item?.productName === "OCROffline"
                          ? OCR
                          : ""
                      }
                    />
                    <div
                      css={css`
                        margin-top: 22px;
                        padding: 0 0 0 40px;
                        width: 200px;
                        font-size: 12px;
                        line-height: 20px;
                        text-align: left;
                      `}
                    >
                      <p
                        css={css`
                          margin-bottom: 5px;
                        `}
                      >
                        {item?.packageName}
                      </p>
                      <p
                        css={css`
                          margin-bottom: 5px;
                        `}
                      >
                        {formatMessage(
                          messageIds.installation.common.versionId
                        )}
                        : {item?.version}
                      </p>
                      <p
                        css={css`
                          margin-bottom: 5px;
                        `}
                      >
                        {formatMessage(
                          messageIds.installation.common.publishTime
                        )}
                        : {moment(item?.publishedAt).format("YYYY/MM/DD")}
                      </p>
                    </div>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-around;
                        align-items: center;
                        margin-top: 10px;
                      `}
                    >
                      <Button
                        type="link"
                        onClick={() => onDelete(item as InstallPackageList)}
                      >
                        {formatMessage(messageIds.common.delete)}
                      </Button>
                      <Divider type="vertical"></Divider>
                      <Button
                        type="link"
                        onClick={() => onIsUpdate(item as InstallPackageList)}
                      >
                        {formatMessage(messageIds.installation.common.update)}
                      </Button>
                    </div>
                  </div>
                </List.Item>
              </div>
            )}
          ></List>
        </div>
      )}
      <UpdatePackage
        visible={isModalShow}
        onClose={onClose}
        packageData={packageData}
        list={list}
      ></UpdatePackage>
    </div>
  );
});
